import { useMemo } from "react";
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CountPill from "../CountPill";
import DuplicateOfTag from "../DuplicateOfTag";
import FetcherTablePagination from "../FetcherTablePagination";
import { SortDirection, sortRows } from "../helpers/tableSorting";

export interface ReportTableRow {
  id: string;
  name: string;
  interfaceName: string;
  collectionType: string;
  urlPath: string;
  configs: number;
  objects: number;
  brands: number;
  twinIds: string[];
}

interface Props {
  rows: ReportTableRow[];
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (key: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  canEdit: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onShowBrands: (id: string) => void;
  onOpenTwin: (id: string) => void;
}

const headCells: { key: string; label: string; sortable: boolean }[] = [
  { key: "id", label: "Report ID", sortable: true },
  { key: "name", label: "Report Name", sortable: true },
  { key: "interfaceName", label: "Interface", sortable: true },
  { key: "collectionType", label: "Collection Type", sortable: true },
  { key: "urlPath", label: "URL Path", sortable: false },
  { key: "configs", label: "Configs", sortable: true },
  { key: "objects", label: "Objects", sortable: true },
  { key: "brands", label: "Brands", sortable: true },
  { key: "actions", label: "Quick Actions", sortable: false },
];

const ReportsTable = ({
  rows,
  sortKey,
  sortDirection,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  canEdit,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onShowBrands,
  onOpenTwin,
}: Props) => {
  const sorted = useMemo(
    () => sortRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection],
  );
  const pageRows = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <>
      <TableContainer>
        <Table size="small" stickyHeader sx={{ whiteSpace: "nowrap" }}>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.key}
                  align={cell.key === "actions" ? "right" : "left"}
                >
                  {cell.sortable ? (
                    <TableSortLabel
                      active={sortKey === cell.key}
                      direction={sortKey === cell.key ? sortDirection : "asc"}
                      onClick={() => onSort(cell.key)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  ) : (
                    cell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "#00000099", py: 3 }}
                  >
                    No reports match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                className="fetcher-row-link"
                onClick={() => onView(row.id)}
                data-tracker="1117"
              >
                <TableCell className="fetcher-mono">{row.id}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                    <DuplicateOfTag
                      ids={row.twinIds}
                      prefix="POSSIBLE DUPLICATE OF"
                      title="Advisory only - matches on interface, collection type and the name with a copy suffix (_new, _old, _copy, _temp, _update) removed"
                      onClick={() => onOpenTwin(row.twinIds[0])}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      backgroundColor: "secondary.main",
                      color: "secondary.contrastText",
                      borderRadius: "14px",
                      padding: "3px 11px",
                      fontSize: "11.5px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.interfaceName}
                  </Box>
                </TableCell>
                <TableCell>{row.collectionType}</TableCell>
                <TableCell
                  className="fetcher-mono"
                  sx={{ fontSize: "12px", color: "#00000099" }}
                >
                  {row.urlPath || "N/A"}
                </TableCell>
                <TableCell>
                  <CountPill value={row.configs} />
                </TableCell>
                <TableCell>
                  <CountPill value={row.objects} variant="objects" />
                </TableCell>
                <TableCell
                  data-tracker="1112"
                  onClick={(event) => {
                    if (row.brands === 0) return;
                    event.stopPropagation();
                    onShowBrands(row.id);
                  }}
                  sx={{
                    cursor: row.brands > 0 ? "pointer" : "default",
                    "&:hover .fetcher-used-hint": { opacity: 1 },
                    ...(row.brands > 0 && {
                      "&:hover": { backgroundColor: "#F2E6F7" },
                    }),
                  }}
                >
                  <CountPill value={row.brands} clickable />
                  {row.brands > 0 && (
                    <Box
                      component="span"
                      className="fetcher-used-hint"
                      sx={{
                        fontSize: "11px",
                        color: "secondary.main",
                        textDecoration: "underline",
                        marginLeft: "4px",
                        opacity: 0,
                        transition: "opacity 0.15s",
                      }}
                    >
                      view
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip title={canEdit ? "Edit" : "View"}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(row.id)}
                        disabled
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <>
                        <Tooltip title="Duplicate">
                          <IconButton
                            size="small"
                            onClick={() => onDuplicate(row.id)}
                            disabled
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => onDelete(row.id)}
                            disabled
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FetcherTablePagination
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};

export default ReportsTable;
